import React from "react";
import startCase from "lodash/startCase";
import get from "lodash/get";
import dynamic from "next/dynamic";
import { scrollTopForId } from "utils/scroll";
import { EDITOR_MODE } from "utils/constants/editor";
import {
  EDITOR_PREVIEW_ID,
  STATUS,
  TOOLTIP_POSITION
} from "utils/constants/ui";
import { VIEWPORT_SIZE } from "utils/constants/viewport";
import { useFormikContext } from "formik";
import { STATE_KEYS } from "utils/constants/state";
import { useIsManifestEditMode } from "utils/hooks/manifest";
import HoverIconButton from "components/HoverIconButton";
import CommandPalette from "components/CommandPalette";
import Trigger from "components/CommandPalette/Trigger";
import { useManifestCommands } from "utils/hooks/command";
import { COMMAND_ITEM_ID } from "utils/hooks/command/form";

const IconButtonBarLoader = () => <span></span>;
const IconButtonBar = dynamic(() => import("components/IconButtonBar"), {
  ssr: false,
  loading: IconButtonBarLoader
});

const MODE_CONFIG = [
  {
    icon: EDITOR_MODE.USE,
    value: EDITOR_MODE.USE
  },
  {
    icon: EDITOR_MODE.INSPECT,
    value: EDITOR_MODE.INSPECT
  },
  {
    icon: EDITOR_MODE.EDIT,
    value: EDITOR_MODE.EDIT
  }
];

const VIEWPORT_CONFIG = [
  {
    icon: VIEWPORT_SIZE.DESKTOP,
    value: VIEWPORT_SIZE.DESKTOP
  },
  {
    icon: VIEWPORT_SIZE.MOBILE,
    value: VIEWPORT_SIZE.MOBILE
  }
];

const DEFAULT_CLASSES = {
  wrapper: "w-100 flex flex-column bg-content--secondary",
  container: "w-100 flex flex-row justify-between",
  controls: "flex flex-row justify-end w-100",
  left: "h-100 pr2 mr2 flex flex-row br hairline-1",
  right: "h-100 pr2 flex flex-row",
  statusMessageBar: "flex flex-row justify-end w-100 pv2 bt hairline-1",
  statusMessage: "tr f7 black-60 pr2 fw5"
};

const STATUS_MESSAGE = {
  EDIT_MODE: "Some controls are disabled in text edit mode."
};

const EditorToolbar = ({ customClasses }) => {
  const classes = { ...DEFAULT_CLASSES, ...customClasses };
  const { values, setFieldValue } = useFormikContext();

  const isEditMode = useIsManifestEditMode();
  let statusMessage;
  if (isEditMode) {
    statusMessage = STATUS_MESSAGE.EDIT_MODE;
  }

  const commands = useManifestCommands();

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <div className="pl2 flex flex-row">
          <CommandPalette commands={commands} />
          <div className="pl1 flex">
            <CommandPalette
              preventKeyboardTrigger
              trigger={
                <Trigger copy="Tutorials" icon="play" status={STATUS.NEUTRAL} />
              }
              initialValues={{ history: [COMMAND_ITEM_ID.OTHER.TUTORIALS] }}
            />
          </div>
        </div>
        <div className={classes.controls}>
          <div className={classes.left}>
            <div className="dib mr2 pr2 hairline-1 br">
              <HoverIconButton
                icon="up"
                tooltip="Scroll top"
                onClick={() => {
                  scrollTopForId(EDITOR_PREVIEW_ID);
                }}
              />
            </div>
            <IconButtonBar
              tooltipPosition={TOOLTIP_POSITION.LEFT}
              value={get(values, STATE_KEYS.EDITOR.MODE)}
              onClick={(updatedMode) => {
                setFieldValue(STATE_KEYS.EDITOR.MODE, updatedMode);
              }}
              config={MODE_CONFIG}
              tooltipFormatter={(value) => `${startCase(value)} mode`}
            />
          </div>
          <div className={classes.right}>
            <IconButtonBar
              tooltipPosition={TOOLTIP_POSITION.LEFT}
              value={get(values, STATE_KEYS.EDITOR.VIEWPORT)}
              onClick={(updatedViewport) => {
                setFieldValue(STATE_KEYS.EDITOR.VIEWPORT, updatedViewport);
              }}
              config={VIEWPORT_CONFIG}
            />
          </div>
        </div>
      </div>
      {statusMessage && (
        <div className={classes.statusMessageBar}>
          <div className={classes.statusMessage}>{statusMessage}</div>
          <div
            onClick={() =>
              setFieldValue(STATE_KEYS.EDITOR.MODE, EDITOR_MODE.INSPECT)
            }
            className={`${classes.statusMessage} link__underline color-primary pointer fw6`}
          >
            Done
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorToolbar;
